import React, { useLayoutEffect, useRef } from 'react';
import { cx } from '@milanote/roma/utils';

import {
    IMAGE_MODAL_V2_TOOLBAR_EXIT_FALLBACK_MS,
    IMAGE_MODAL_V2_TOOLBAR_MOTION_DURATION_MS,
} from '../imageModalV2Constants';
import { useImageModalV2SurfaceLifecycle } from './useImageModalV2SurfaceLifecycle';

type ImageModalV2ToolbarSurfaceProps = {
    isOpen: boolean;
    motion: 'primary' | 'secondary';
    className: string;
    style?: React.CSSProperties;
    skipInitialEnter?: boolean;
    onExitComplete?: () => void;
    children: React.ReactNode;
};

/**
 * Keeps one toolbar surface mounted while applying reversible modal-local opacity and Y motion.
 */
const ImageModalV2ToolbarSurface: React.FC<ImageModalV2ToolbarSurfaceProps> = ({
    isOpen,
    motion,
    className,
    style,
    skipInitialEnter = false,
    onExitComplete,
    children,
}) => {
    const motionRef = useRef<HTMLDivElement>(null);
    const { isShown, handleTransitionEnd } = useImageModalV2SurfaceLifecycle({
        isOpen,
        fallbackMs: IMAGE_MODAL_V2_TOOLBAR_MOTION_DURATION_MS + IMAGE_MODAL_V2_TOOLBAR_EXIT_FALLBACK_MS,
        transitionProperty: 'opacity',
        requireOwnTarget: true,
        skipInitialEnter,
        guardNeverOpened: true,
        onExitComplete,
    });

    // `aria-hidden` alone leaves the hidden/exiting surface's buttons in the tab order, so a keyboard user
    // could focus an invisible toolbar mid-transition. `inert` removes the subtree from focus and the a11y
    // tree while it is not shown. (Set via the DOM API — `inert` is not typed in this React version.)
    useLayoutEffect(() => {
        motionRef.current?.toggleAttribute('inert', !isShown);
    }, [isShown]);

    return (
        <div className={className} style={style} aria-hidden={!isShown}>
            <div
                ref={motionRef}
                className={cx(
                    'ImageModalV2ToolbarSurface-motion',
                    `ImageModalV2ToolbarSurface-motion-${motion}`,
                    isShown && 'is-shown',
                    !isShown && 'pointer-events-none [&_*]:!pointer-events-none',
                )}
                onTransitionEnd={handleTransitionEnd}
            >
                {children}
            </div>
        </div>
    );
};

export default ImageModalV2ToolbarSurface;
